import {
  buildPlaceDetailPath as buildPolicyPlaceDetailPath,
  buildPlayDetailPath as buildPolicyPlayDetailPath,
  buildPostDetailPath as buildPolicyPostDetailPath,
  normalizeRouteSegment,
} from "../utils/url-policy.mjs";
import {
  getContentDetailPath,
  isVideoContent,
} from "../utils/content-adapter.mjs";
import { getPlaceIdentifier } from "../utils/placeUtils.js";

export { normalizeRouteSegment, isVideoContent };

export const ROUTES = Object.freeze({
  current: Object.freeze({
    home: "/play",
    play: "/play",
    search: "/search",
    place: "/places",
  }),
  future: Object.freeze({
    play: "/play",
    place: "/place",
    profile: "/profile",
    publicProfilePrefix: "/@",
  }),
  legacy: Object.freeze({
    hot: "/hot",
    places: "/places",
  }),
  auth: Object.freeze({
    accountRecovery: "/account-recovery",
    forgotPassword: "/forgot-password",
    resetPassword: "/reset-password",
  }),
  creator: Object.freeze({
    home: "/creator",
    // 영상 올리는 화면. 2026-08-11 에 "릴리스 노트" 라는 옛 이름을 걷어냈다 —
    // 화면·폴더·주소 전부. 옛 주소 두 개는 proxy.js 가 301 로 넘긴다.
    upload: "/creator/upload",
    uploadEdit: "/creator/upload/edit",
    noteManager: "/creator/note-manager",
    accountOverview: "/creator/account-overview",
    profileSettings: "/creator/profile-settings",
  }),
  admin: Object.freeze({
    root: "/admin",
    dataCenter: "/admin/data-center",
  }),
  user: Object.freeze({
    notifications: "/notify",
    messages: "/messages",
    plans: "/plans",
  }),
});

const LANGUAGE_RESERVED_PROFILE_SEGMENTS = Object.freeze([
  "af",
  "ar",
  "az",
  "be",
  "bg",
  "bn",
  "bs",
  "ca",
  "cs",
  "cy",
  "da",
  "de",
  "el",
  "en",
  "en-gb",
  "en-us",
  "es",
  "es-es",
  "es-mx",
  "et",
  "eu",
  "fa",
  "fi",
  "fr",
  "fr-fr",
  "ga",
  "gl",
  "gu",
  "he",
  "hi",
  "hr",
  "hu",
  "hy",
  "id",
  "id-id",
  "is",
  "it",
  "it-it",
  "ja",
  "ja-jp",
  "ka",
  "kk",
  "kn",
  "ko",
  "ko-kr",
  "lt",
  "lv",
  "mk",
  "ml",
  "mn",
  "mr",
  "ms",
  "nl",
  "no",
  "pa",
  "pl",
  "pt",
  "pt-br",
  "pt-pt",
  "ro",
  "ru",
  "ru-ru",
  "sk",
  "sl",
  "sq",
  "sr",
  "sv",
  "ta",
  "te",
  "th",
  "th-th",
  "tr",
  "uk",
  "ur",
  "uz",
  "vi",
  "vi-vn",
  "zh",
  "zh-cn",
  "zh-tw",
]);

export const PROFILE_RESERVED_SEGMENTS = Object.freeze([
  ...LANGUAGE_RESERVED_PROFILE_SEGMENTS,
  "hot",
  "play",
  "place",
  "profile",
  "places",
  "places-new",
  "admin",
  "creator",
  "notify",
  "messages",
  "plans",
  "events",
  "album",
  "account-recovery",
  "forgot-password",
  "reset-password",
  "retrieve-username",
  "search",
  "test",
  "test-search",
  "test-places",
  "api",
  "_next",
  "sitemap.xml",
  "sitemaps",
  "robots.txt",
  "manifest.json",
  "favicon.ico",
  "apple-icon.png",
  "icon0.svg",
  "icon1.png",
]);

export const normalizeProfileIdentifier = (username, userId) =>
  normalizeRouteSegment(username || userId);

export const isReservedProfileIdentifier = (identifier) =>
  PROFILE_RESERVED_SEGMENTS.includes(normalizeRouteSegment(identifier));

export const buildCurrentUserProfileIdentifier = (username, userId) =>
  normalizeProfileIdentifier(username, userId);

export const buildCurrentUserProfilePath = (username, userId) => {
  const identifier = buildCurrentUserProfileIdentifier(username, userId);
  return identifier ? `${ROUTES.future.publicProfilePrefix}${identifier}` : "";
};

const isCurrentRouteOrDetailPath = (pathname, route) =>
  pathname === route ||
  (typeof pathname === "string" && pathname.startsWith(`${route}/`));

export const isCurrentPlayFeedPath = (pathname) => pathname === ROUTES.current.play;

export const isCurrentPlayPath = (pathname) =>
  isCurrentRouteOrDetailPath(pathname, ROUTES.current.play);

export const isCurrentFeedPath = (pathname) => isCurrentPlayPath(pathname);

export const isCurrentPlacePath = (pathname) =>
  typeof pathname === "string" &&
  (pathname === ROUTES.current.place ||
    pathname.startsWith(`${ROUTES.current.place}/`) ||
    pathname === ROUTES.future.place ||
    pathname.startsWith(`${ROUTES.future.place}/`));

export const isCurrentPlaceDetailPath = (pathname) =>
  typeof pathname === "string" && pathname.startsWith(`${ROUTES.future.place}/`);

export const isCompactUserLayoutPath = () => false;

const appendQuery = (basePath, params = {}) => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") return;
    searchParams.set(key, String(value));
  });

  const query = searchParams.toString();
  return query ? `${basePath}?${query}` : basePath;
};

const stripQueryAndHash = (path) =>
  typeof path === "string" ? path.split("?")[0].split("#")[0] : path;

export const buildPlaySearchPath = (search, params = {}) =>
  appendQuery(ROUTES.current.search, { q: search, ...params });

const isLikelyMongoObjectId = (value) => /^[a-f\d]{24}$/i.test(String(value || ""));

export const buildPlayReturnPath = (postOrPublicId) => {
  if (!postOrPublicId) return ROUTES.current.play;

  if (typeof postOrPublicId === "string") {
    return isLikelyMongoObjectId(postOrPublicId)
      ? ROUTES.current.play
      : buildPlayDetailPath(postOrPublicId);
  }

  if (postOrPublicId.publicId) return buildPlayDetailPath(postOrPublicId.publicId);
  return ROUTES.current.play;
};

export const buildPostDetailPath = (postOrPublicId) => {
  const path = buildPolicyPostDetailPath(postOrPublicId);
  return path === "/hot" ? ROUTES.current.play : path;
};

export const buildPlayDetailPath = (postOrPublicId) => {
  const path = buildPolicyPlayDetailPath(postOrPublicId);
  return path === "/play" ? ROUTES.current.play : path;
};

export const buildContentPermanentPath = (content) =>
  getContentDetailPath(content);

const LEGACY_PATH_REPLACEMENTS = Object.freeze({
  "/": ROUTES.current.play,
  "/hot": ROUTES.current.play,
});

export const normalizeLegacyPath = (target) => {
  if (typeof target !== "string" || !target) return target;
  if (/^[a-z][a-z\d+\-.]*:/i.test(target)) return target;

  const [pathWithQuery, hash = ""] = target.split("#");
  const [pathname, query = ""] = pathWithQuery.split("?");
  const normalizedPathname = LEGACY_PATH_REPLACEMENTS[pathname] || pathname;

  return `${normalizedPathname}${query ? `?${query}` : ""}${
    hash ? `#${hash}` : ""
  }`;
};

export const buildPlaceListPath = (params) =>
  appendQuery(ROUTES.current.place, params);

export const getSelectedPlaceMapId = (placeOrId) => {
  if (!placeOrId) return "";
  if (typeof placeOrId !== "object") return placeOrId;

  return (
    getPlaceIdentifier(placeOrId) ||
    placeOrId?.placeUniqueNumber ||
    placeOrId?.contentid ||
    placeOrId?.contentId ||
    placeOrId?.publicId ||
    ""
  );
};

export const buildSelectedPlaceMapPath = (placeOrId, params = {}) => {
  const placeId = getSelectedPlaceMapId(placeOrId);
  const {
    "view-place": _viewPlace,
    "view-place-in-list": _viewPlaceInList,
    ...restParams
  } = params || {};

  return buildPlaceListPath({
    ...restParams,
    "view-place": placeId,
  });
};

export const buildPlaceDetailPath = (placeOrId) => {
  const path = buildPolicyPlaceDetailPath(placeOrId);
  return path === "/places" ? ROUTES.current.place : path;
};
